import { selOrganizationUsers } from '@features/st-organizations/st-organization-module'
import type { PageGroup } from '@st/sdk'
import { useCurrentUser } from '@features/app-hooks'
import { useDocumentUploads } from '~/features/document-uploads/use-document-uploads'
import { Link } from '@features/link'
import { useDevMode } from '@st/react-util/dev-mode'
import type {
  Folder,
  FolderTag,
  Provider,
  PullTaxReturnResult,
  ScopedFolderMembership,
  STDocument
} from '@st/sdk'
import {
  ActivityFeedItem,
  ActivityFeedList,
  ArrowRightIcon,
  Banner,
  Button,
  ContextMenuItem,
  Dialog,
  DialogButtons,
  DownloadIcon,
  EyeIcon,
  GearIcon,
  FullScreenDropZoneBarrier,
  LinkIcon,
  Modal,
  RefreshIcon,
  PopoverMenu,
  PrimaryContextMenu,
  RowsIcon,
  SendIconLarge,
  Select,
  Spinner,
  TagChip,
  TextInput,
  Tip,
  ToolbarSectionDivider,
  useFileDrop
} from '@st/theme'
import { formatPercentage } from '@st/util/format'
import { parseMimeType } from '@st/util/mime'
import { formatISO8601RelativeDateTime } from '@st/util/time'
import { captureUpload } from '@st/util/archive/browser-upload'
import { useState } from 'react'
import { match, P } from '@st/pattern'
import { STPDFDocViewer } from '~/st-doc-viewer'
import { useProcess, useSelect } from '~/store'
import { getStatusColor, getStatusLabel } from '../st-folder-list/common'
import {
  selOrganizationFolderTags,
  stOrganizationModule
} from '../st-organizations/st-organization-module'
import { EditFolderTagsDialog } from './edit-folder-tags-dialog'
import { FolderTagsList, FolderTagsListEditButton } from './folder-tags-list'
import { STDocumentSidebar } from './st-document-sidebar'
import { STDownloadFolderDialog } from './st-download-folder-dialog'
import {
  openDownloadFolderDialog,
  stDownloadFolderModule,
  STDownloadFolderModuleState
} from './st-download-folder-module'
import {
  selActivityFeedEvents,
  selBulkUpload,
  selFolderState,
  selFolderViewerHeader,
  selSelectedDoc,
  type ExpandedActivityFeedEvent
} from './st-folder-selectors'
import { formatFolderEntityName } from './st-folder-display'
import { type STFolderMessage } from './st-folder-module'
import type { TaxReturnPullStatusDialogState } from './st-folder-types'
import {
  openSendClientOrganizerDialog,
  openShareableLinkDialog
} from './st-send-client-organizer-module'
import { StFolderViewerDebug } from './st-folder-viewer-debug'
import { resolveDocumentView } from './document-viewer-extensions'
import { useDocumentViewerState } from './document-viewer-extensions/use-document-viewer-state'
import { STFormationChecklistViewer } from './st-formation-checklist-viewer'
import { STFormationViewer } from './st-formation-viewer'
import { updatedDocumentPageRotations } from './update-document-page-rotations'
import { updatedPageGroups } from './update-page-groups'
import { humanJoin } from '@st/util/inflect'
import { BulkUploadDialog } from './bulk-upload/bulk-upload-dialog'
import { DeliveryWizardModal } from './document-viewer-extensions/tax-return-delivery/delivery-wizard-dialog'
import { KarbonDeliveryModal } from './karbon-delivery-dialog'
import { PaymentLinkDialog } from './document-viewer-extensions/tax-return-delivery/payment-link-dialog'
import { useCaptureBrowserResource } from '~/features/browser-resources/browser-resource-boundary'
import { classifyUploadSelection } from '../document-uploads/detect-upload-type'
import { FEATURE_FLAGS } from '~/env'

type FolderAction =
  | { type: 'copyWorkpaperLink' }
  | { type: 'previewQuestionnaire' }
  | { type: 'uploadDocuments'; files: File[] }
  | { type: 'deleteAllDocuments' }
  | { type: 'editFolderTags' }
  | { type: 'linkTaxReturn'; provider: Provider | undefined; providerId: string }

const LINK_TAX_RETURN_PROVIDERS: Provider[] = ['ultratax']

type Props = {
  folderId: string
  selectedDocId?: string
  onSelect?: (doc: STDocument) => void
  debug?: boolean
}
export function STFolderViewer({ folderId, debug }: Props) {
  const user = useCurrentUser()

  const organizationFolderTags = useSelect('stOrganization', selOrganizationFolderTags)

  const documentUploads = useDocumentUploads()
  const stFolder = useProcess('stFolder')
  const stDownloadFolder = useProcess('stDownloadFolder')
  const captureBrowserResource = useCaptureBrowserResource()
  const location = useSelect('route', (s) => s.location)
  const currentHref = `${location.origin}${location.pathname}`

  const folderHeader = useSelect('stFolder', selFolderViewerHeader)
  const selectedDoc = useSelect('stFolder', selSelectedDoc)
  const bulkUpload = useSelect('stFolder', selBulkUpload)
  const taxReturnPullStatusDialog = useSelect('stFolder', (state) => {
    if (state.status != 'ready') return undefined
    return state.taxReturnPullStatusDialog
  })
  const downloadFolderStatus = useSelect('stDownloadFolder', (s) => s.status)
  const [action, setAction] = useState<FolderAction | undefined>()

  if (debug) {
    return <StFolderViewerDebug />
  }

  const handleAction = (action: FolderAction | undefined) => {
    switch (action?.type) {
      case 'copyWorkpaperLink':
        stFolder.send({ type: 'copyWorkpaperLink', url: currentHref })
        break
      case 'previewQuestionnaire':
        stFolder.send({ type: 'previewQuestionnaire' })
        break
      case 'uploadDocuments':
        for (const file of action.files) {
          documentUploads.upload({ file, folderId })
        }
        break
      case 'deleteAllDocuments':
        stFolder.send({ type: 'deleteAllDocuments' })
        break
      default:
        setAction(action)
    }
  }

  const handleDrop = (dataTransfer: DataTransfer) => {
    const upload = captureUpload(dataTransfer)
    const kind = classifyUploadSelection(upload)
    switch (kind.source) {
      case 'file_list':
        handleAction({ type: 'uploadDocuments', files: kind.files.map((entry) => entry.file) })
        break
      case 'zip':
      case 'folder':
        stFolder.send({
          type: 'bulkUpload',
          message: {
            type: 'started',
            upload: captureBrowserResource(upload),
            source: kind.source
          }
        })
        break
    }
  }

  const handleBulkUpload = (selectedFilePaths: string[]) => {
    if (bulkUpload?.status != 'resolved') return

    const files = [...bulkUpload.entries.uniqueFiles, ...bulkUpload.entries.duplicates].filter(
      (entry) => selectedFilePaths.includes(entry.path)
    )
    documentUploads.uploadSelection({ filesResource: bulkUpload.files, files, folderId })
  }

  return (
    <>
      <STFolderViewerInner
        folderEntities={folderHeader.folderEntities}
        folderType={folderHeader.folderType}
        action={action}
        folderId={folderId}
        clientOrganizerStatus={folderHeader.clientOrganizerStatus}
        questionnaireProgress={folderHeader.questionnaireProgress}
        selectedDoc={selectedDoc}
        loading={!folderHeader.folderLoaded}
        organizationFolderTags={organizationFolderTags}
        folderTagIds={folderHeader.folderTagIds}
        bulkUpload={bulkUpload}
        isArchived={folderHeader.isArchived}
        sourceName={folderHeader.sourceName}
        sourceId={folderHeader.sourceId}
        taxReturnId={folderHeader.taxReturnId}
        taxReturnLastPulledAt={folderHeader.taxReturnLastPulledAt}
        taxReturnRefreshStatus={folderHeader.taxReturnRefreshStatus}
        taxReturnPullStatusDialog={taxReturnPullStatusDialog}
        onAction={handleAction}
        onDrop={handleDrop}
        onBulkUpload={handleBulkUpload}
        onCancelBulkUpload={() =>
          stFolder.send({ type: 'bulkUpload', message: { type: 'closed' } })
        }
        onDownloadFolder={() =>
          stDownloadFolder.send(openDownloadFolderDialog({ folderId: folderId }))
        }
        downloadFolderStatus={downloadFolderStatus}
        send={stFolder.send}
      />
    </>
  )
}

function STFolderViewerInner({
  folderId,
  folderEntities,
  folderType,
  selectedDoc,
  clientOrganizerStatus,
  questionnaireProgress,
  action,
  organizationFolderTags,
  folderTagIds,
  bulkUpload,
  loading,
  isArchived,
  sourceName,
  sourceId,
  taxReturnId,
  taxReturnLastPulledAt,
  taxReturnRefreshStatus,
  taxReturnPullStatusDialog,
  onAction,
  onDrop,
  onBulkUpload,
  onCancelBulkUpload,
  onDownloadFolder,
  downloadFolderStatus,
  send
}: {
  folderId: string
  folderEntities: Folder['entities'] | undefined
  folderType: string | undefined
  clientOrganizerStatus: Folder['clientOrganizer']['status'] | undefined
  questionnaireProgress: Folder['clientOrganizer']['questionnaireProgress']
  selectedDoc: STDocument | undefined
  action: FolderAction | undefined
  organizationFolderTags: FolderTag[]
  folderTagIds: string[]
  bulkUpload: ReturnType<typeof selBulkUpload>
  loading: boolean
  isArchived: boolean
  sourceName: string | undefined
  sourceId: string | undefined
  taxReturnId: string | undefined
  taxReturnLastPulledAt: string | undefined
  taxReturnRefreshStatus: 'idle' | 'running'
  taxReturnPullStatusDialog: TaxReturnPullStatusDialogState | undefined
  onAction: (action: FolderAction | undefined) => void
  onDrop: (dataTransfer: DataTransfer) => void
  onBulkUpload: (selectedFilePaths: string[]) => void
  onCancelBulkUpload: () => void
  onDownloadFolder: () => void
  downloadFolderStatus: STDownloadFolderModuleState['status']
  send: (message: STFolderMessage) => void
}) {
  const stSendClientOrganizer = useProcess('stSendClientOrganizer')
  const organizationUsers = useSelect('stOrganization', selOrganizationUsers)
  const organizerSettings = useProcess('folderOrganizerSettings')
  // Disables drag-and-drop behavior entirely when archived.
  const { isDragging, dropHandlers } = useFileDrop(onDrop, { disabled: isArchived })
  const devMode = useDevMode()

  const binderBanner = match({ isArchived })
    .with({ isArchived: true }, () => (
      <Banner variant="warning" className="rounded-none border-b border-stone-200">
        {`This binder is archived and read-only. You must unarchive this binder in order to upload or delete documents.`}
      </Banner>
    ))
    .with({ isArchived: false }, () => null)
    .exhaustive()

  const entityName = formatFolderEntityName(folderEntities, 'client')
  const taxReturnRefreshRunning = taxReturnRefreshStatus == 'running'
  const canUseTaxReturnActions =
    FEATURE_FLAGS.pushTaxReturn && !isArchived && !loading && !taxReturnRefreshRunning
  const canLinkTaxReturn = canUseTaxReturnActions && !taxReturnId
  const showTaxReturnSection = FEATURE_FLAGS.pushTaxReturn && taxReturnId != undefined
  const taxReturnRefreshTip = taxReturnLastPulledAt
    ? `Last synced ${formatISO8601RelativeDateTime(taxReturnLastPulledAt)}`
    : 'Not synced yet'

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden"
      style={{ minWidth: '1200px' }}
      {...dropHandlers}
    >
      {isDragging && <FullScreenDropZoneBarrier />}
      <div className="bg-white-200 flex flex-row items-center justify-between border-b border-stone-200 px-5 py-3">
        {/* Breadcrumb */}
        <div className="group flex flex-row items-center gap-2">
          <Link href={{ name: 'folder_list' }} className="text-lg text-gray-600">
            Binders
          </Link>
          {entityName ? <ArrowRightIcon className="size-4 text-gray-500" /> : null}
          {entityName ? (
            <span className="text-lg font-bold text-gray-900">{`${entityName}'s Binder`}</span>
          ) : null}

          <div className="ml-2 flex flex-row items-center gap-2">
            <FolderTagsList
              folderTags={organizationFolderTags}
              tagIds={folderTagIds}
              onClick={() => onAction({ type: 'editFolderTags' })}
            />

            <FolderTagsListEditButton onClick={() => onAction({ type: 'editFolderTags' })} />
          </div>
        </div>

        <div className="flex flex-row items-stretch gap-2">
          <div className="flex flex-row items-center gap-2">
            <div className="text-sm text-gray-500">Client Organizer</div>

            {clientOrganizerStatus && (
              <TagChip
                color={getStatusColor(clientOrganizerStatus)}
                className="flex items-center gap-2"
              >
                {getStatusLabel(clientOrganizerStatus)}
                {clientOrganizerStatus == 'in_progress' && (
                  <div>{formatPercentage(questionnaireProgress)}%</div>
                )}
              </TagChip>
            )}

            <Tip title="Preview client organizer">
              <Button
                variant="icon"
                aria-label="Preview client organizer"
                leadingIcon={<EyeIcon className="size-4" />}
                onClick={() => onAction({ type: 'previewQuestionnaire' })}
                disabled={loading}
              />
            </Tip>

            <Tip title="Shareable client organizer link">
              <Button
                variant="icon"
                aria-label="Copy shareable client organizer link"
                leadingIcon={<LinkIcon className="size-4" />}
                onClick={() =>
                  stSendClientOrganizer.send(openShareableLinkDialog(folderId, organizationUsers))
                }
                disabled={loading}
              />
            </Tip>

            <Tip title="Send to client">
              <Button
                variant="icon"
                aria-label="Send client organizer to client"
                leadingIcon={<SendIconLarge className="size-4" />}
                onClick={() =>
                  stSendClientOrganizer.send(
                    openSendClientOrganizerDialog({
                      users: organizationUsers,
                      folderId,
                      type: 'clientOrganizerEmail',
                      messageType: 'client_organizer_invitation'
                    })
                  )
                }
                disabled={loading}
              />
            </Tip>

            {folderType && (
              <Tip title="Client organizer settings">
                <Button
                  variant="icon"
                  aria-label="Client organizer settings"
                  leadingIcon={<GearIcon className="size-4" />}
                  onClick={() =>
                    organizerSettings.send({ type: 'open', folderId, returnType: folderType })
                  }
                  disabled={loading}
                />
              </Tip>
            )}
          </div>

          <ToolbarSectionDivider />

          {showTaxReturnSection ? (
            <>
              <div className="flex flex-row items-center gap-2">
                <div className="text-sm text-gray-500">Tax Return</div>
                <Tip title={taxReturnRefreshTip}>
                  <Button
                    variant="icon"
                    aria-label="Pull latest UltraTax return"
                    leadingIcon={
                      taxReturnRefreshRunning ? (
                        <Spinner className="size-4" />
                      ) : (
                        <RefreshIcon className="size-4" />
                      )
                    }
                    onClick={() => send({ type: 'refreshTaxReturn' })}
                    disabled={!canUseTaxReturnActions}
                  />
                </Tip>
              </div>

              <ToolbarSectionDivider />
            </>
          ) : null}

          <div className="flex flex-row items-center gap-2">
            <div className="text-sm text-gray-500">Binder</div>
            <Tip title="Export binder">
              <Button
                variant="icon"
                aria-label="Export binder"
                leadingIcon={<DownloadIcon className="size-4" />}
                onClick={onDownloadFolder}
                disabled={loading}
              />
            </Tip>

            <ActivityFeedButton />

            <PrimaryContextMenu aria-label="Binder actions">
              {clientOrganizerStatus == 'submitted' ? (
                <ContextMenuItem
                  onClick={() =>
                    send({
                      type: 'unlockClientOrganizer',
                      request: { type: 'folders/unlockClientOrganizer', folderId: folderId }
                    })
                  }
                >
                  Unsubmit client organizer
                </ContextMenuItem>
              ) : (
                <ContextMenuItem
                  onClick={() =>
                    send({
                      type: 'requestEnqueued',
                      request: {
                        type: 'folders/submitClientOrganizer',
                        folderId: folderId,
                        forceSubmit: true
                      }
                    })
                  }
                >
                  Submit organizer on behalf of client
                </ContextMenuItem>
              )}

              <ContextMenuItem onClick={() => onAction({ type: 'copyWorkpaperLink' })}>
                Copy firm workpaper link
              </ContextMenuItem>

              {canLinkTaxReturn && (
                <ContextMenuItem
                  onClick={() =>
                    onAction({
                      type: 'linkTaxReturn',
                      provider:
                        resolveLinkTaxReturnProvider(sourceName) ?? defaultLinkTaxReturnProvider(),
                      providerId: sourceId ?? ''
                    })
                  }
                >
                  Link UltraTax return
                </ContextMenuItem>
              )}

              {devMode && (
                <ContextMenuItem onClick={() => onAction({ type: 'deleteAllDocuments' })}>
                  Delete all documents
                </ContextMenuItem>
              )}
            </PrimaryContextMenu>
          </div>
        </div>
      </div>
      {binderBanner}
      {loading && !selectedDoc ? (
        <BinderLoadingState />
      ) : (
        <div className="grid min-h-0 w-full flex-1 grid-cols-[312px_auto] overflow-hidden">
          <STDocumentSidebar />

          {selectedDoc ? (
            <STDocViewer
              key={selectedDoc.id}
              folderId={folderId}
              isArchived={isArchived}
              doc={selectedDoc}
              send={send}
              onAction={onAction}
            />
          ) : (
            <></>
          )}
        </div>
      )}

      <Modal isOpen={action != undefined}>
        {match(action)
          .with(undefined, () => null)
          .with({ type: 'editFolderTags' }, () => (
            <EditFolderTagsDialog
              folderId={folderId}
              tagIds={folderTagIds}
              folderName={formatFolderEntityName(folderEntities, 'client')}
              onSave={({ addTagIds, removeTagIds }) =>
                send({
                  type: 'updateFolderTags',
                  addTagIds,
                  removeTagIds
                })
              }
              onClose={() => onAction(undefined)}
            />
          ))
          .with({ type: 'linkTaxReturn' }, (dialogState) => (
            <LinkTaxReturnDialog
              provider={dialogState.provider}
              providerId={dialogState.providerId}
              onProviderChange={(provider) => onAction({ ...dialogState, provider })}
              onProviderIdChange={(providerId) => onAction({ ...dialogState, providerId })}
              onClose={() => onAction(undefined)}
              onSubmit={() => {
                if (!dialogState.provider || dialogState.providerId.trim().length == 0) {
                  return
                }

                send({
                  type: 'pullInitialTaxReturnAndLink',
                  provider: dialogState.provider,
                  providerId: dialogState.providerId.trim()
                })
                onAction(undefined)
              }}
            />
          ))
          .with(
            P.union(
              { type: 'copyWorkpaperLink' },
              { type: 'previewQuestionnaire' },
              { type: 'uploadDocuments' },
              { type: 'deleteAllDocuments' }
            ),
            () => null
          )
          .exhaustive()}
      </Modal>
      <Modal isOpen={bulkUpload != undefined}>
        {bulkUpload ? (
          <BulkUploadDialog
            entries={bulkUpload}
            onUpload={onBulkUpload}
            onClose={onCancelBulkUpload}
          />
        ) : null}
      </Modal>
      {match(downloadFolderStatus)
        .with('idle', () => null)
        .with(P.union('loading', 'loadFailed', 'loaded'), () => (
          <Modal isOpen={true}>
            <STDownloadFolderDialog />
          </Modal>
        ))
        .exhaustive()}
      {taxReturnPullStatusDialog ? (
        <Modal isOpen={true}>
          <TaxReturnPullStatusDialog
            dialog={taxReturnPullStatusDialog}
            onClose={() => send({ type: 'closeTaxReturnPullStatusDialog' })}
          />
        </Modal>
      ) : null}
      <PaymentLinkDialog />
      <KarbonDeliveryModal />
      <DeliveryWizardModal />
    </div>
  )
}

function LinkTaxReturnDialog({
  provider,
  providerId,
  onProviderChange,
  onProviderIdChange,
  onClose,
  onSubmit
}: {
  provider: Provider | undefined
  providerId: string
  onProviderChange: (provider: Provider | undefined) => void
  onProviderIdChange: (providerId: string) => void
  onClose: () => void
  onSubmit: () => void
}) {
  return (
    <Dialog
      title="Link UltraTax Return"
      subtitle="Select the tax software and enter the client ID for the return you want to pull."
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={!provider || providerId.trim().length == 0}
            onClick={onSubmit}
          >
            Link and Pull
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
          Tax software provider
          <Select
            name="tax-return-provider"
            ariaLabel="Tax software provider"
            dataTestId="tax-return-provider"
            className="w-full"
            placeholder="Select provider"
            options={LINK_TAX_RETURN_PROVIDERS}
            buildValue={(option) => option}
            buildLabel={formatProviderLabel}
            value={provider}
            onChange={onProviderChange}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
          Client ID
          <TextInput
            name="tax-return-client-id"
            ariaLabel="Client ID"
            dataTestId="tax-return-client-id"
            autoFocus={true}
            className="w-full"
            placeholder="Client ID"
            value={providerId}
            onChange={onProviderIdChange}
          />
        </label>
      </div>
    </Dialog>
  )
}

function defaultLinkTaxReturnProvider(): Provider | undefined {
  if (LINK_TAX_RETURN_PROVIDERS.length == 1) {
    return LINK_TAX_RETURN_PROVIDERS[0]
  }
}

function resolveLinkTaxReturnProvider(sourceName: string | undefined): Provider | undefined {
  return sourceName?.toLowerCase() == 'ultratax' ? 'ultratax' : undefined
}

function TaxReturnPullStatusDialog({
  dialog,
  onClose
}: {
  dialog: TaxReturnPullStatusDialogState
  onClose: () => void
}) {
  return (
    <Dialog
      title="Could not pull UltraTax return"
      subtitle={
        'status' in dialog ? resolveTaxReturnPullStatusMessage(dialog.status) : dialog.message
      }
      buttons={
        <DialogButtons>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </DialogButtons>
      }
    />
  )
}

function resolveTaxReturnPullStatusMessage(
  status: Exclude<PullTaxReturnResult['status'], 'pulled'>
): string {
  switch (status) {
    case 'desktop_agent_not_connected':
      return 'Desktop agent is not connected.'
    case 'desktop_agent_busy':
      return 'Desktop agent is busy. Please try again.'
    case 'tax_return_file_not_found':
      return 'UltraTax return not found. Check the client ID and try again.'
    case 'timed_out':
      return 'Timed out waiting for the desktop agent.'
  }
}

function formatProviderLabel(provider: Provider): string {
  switch (provider) {
    case 'ultratax':
      return 'UltraTax'
    default:
      return provider
  }
}

/**
 * Depending on the type of document selected, we detect the appropriate viewer
 * and render that.
 */
function STDocViewer({
  doc,
  folderId,
  isArchived,
  send,
  onAction
}: {
  doc: STDocument
  folderId: string
  isArchived: boolean
  send: (message: STFolderMessage) => void
  onAction: (action: FolderAction | undefined) => void
}) {
  const mime = parseMimeType(doc.mimeType)
  const viewerState = useDocumentViewerState()
  const documentView = resolveDocumentView(viewerState, doc)

  function handleDeletePage(pageNumber: number) {
    send({
      type: 'requestEnqueued',
      request: {
        type: 'folders/deleteDocumentPages',
        folderId: folderId,
        documentId: doc.id,
        pageNumbers: [pageNumber]
      }
    })
  }

  function handleRestorePage(pageNumber: number) {
    send({
      type: 'requestEnqueued',
      request: {
        type: 'folders/restoreDocumentPages',
        folderId: folderId,
        documentId: doc.id,
        pageNumbers: [pageNumber]
      }
    })
  }

  function handleSetPageGroups(groups: PageGroup[]) {
    send({
      type: 'requestEnqueued',
      request: {
        type: 'folders/setDocumentPageGroups',
        folderId,
        documentId: doc.id,
        pageGroups: groups
      }
    })
  }

  function handleUpdatePageGroupBoundary(afterPageNumber: number, action: 'add' | 'remove') {
    handleSetPageGroups(
      updatedPageGroups({ pageGroups: documentView.pageGroups() }, action, afterPageNumber)
    )
  }

  function handleRotatePage(pageNumber: number, rotation: number) {
    send({
      type: 'requestEnqueued',
      request: {
        type: 'folders/updateDocumentPageRotations',
        folderId: folderId,
        documentId: doc.id,
        pageNumbers: [pageNumber],
        rotation: rotation
      }
    })
  }

  function handleRotateDocument(delta: 90 | -90) {
    const batches = updatedDocumentPageRotations({
      pageCount: doc.pageCount,
      pageRotations: doc.pageRotations,
      delta
    })

    for (const batch of batches) {
      send({
        type: 'requestEnqueued',
        request: {
          type: 'folders/updateDocumentPageRotations',
          folderId: folderId,
          documentId: doc.id,
          pageNumbers: batch.pageNumbers,
          rotation: batch.rotation
        }
      })
    }
  }

  return match({ doc, mime })
    .with({ mime: { type: 'application', subtype: 'formation' } }, () => {
      return <STFormationViewer />
    })
    .with({ mime: { type: 'application', subtype: 'formation-checklist' } }, () => {
      return <STFormationChecklistViewer />
    })
    .with({ doc: { processingStatus: 'processing' } }, () => {
      return <DocumentSpinner caption="Processing..." />
    })
    .with({ doc: { passwordProtected: true } }, () => {
      return (
        <DownloadNonViewableFile
          doc={doc}
          send={send}
          warning="This document is password protected."
        />
      )
    })
    .with({ doc: { urls: { view: P.nonNullable } } }, () => {
      return (
        <STPDFDocViewer
          doc={doc}
          folderId={folderId}
          isArchived={isArchived}
          viewerOptions={documentView.viewerOptions()}
          pageGroups={documentView.pageGroups()}
          renderTopBar={(sendViewerMessage) =>
            documentView.renderTopBar({
              isArchived,
              sendViewerMessage
            })
          }
          renderSidebar={(sendViewerMessage, fallback) =>
            documentView.renderSidebar({
              sendViewerMessage,
              fallback
            })
          }
          send={send}
          onDeletePage={handleDeletePage}
          onRestorePage={handleRestorePage}
          onAddPageGroupBoundary={(afterPageNumber) =>
            handleUpdatePageGroupBoundary(afterPageNumber, 'add')
          }
          onRemovePageGroupBoundary={(afterPageNumber) =>
            handleUpdatePageGroupBoundary(afterPageNumber, 'remove')
          }
          onRotatePage={(pageNumber, rotation) => handleRotatePage(pageNumber, rotation)}
          onRotateDocument={(delta) => handleRotateDocument(delta)}
        />
      )
    })
    .with(P._, () => <DownloadNonViewableFile doc={doc} send={send} />)
    .exhaustive()
}

function DownloadNonViewableFile({
  doc,
  send,
  warning
}: {
  doc: STDocument
  send: (message: STFolderMessage) => void
  warning?: string
}) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-stone-200">
      <div className="flex h-1/4 w-1/4 flex-col items-center justify-center rounded-md bg-white p-4 shadow-md">
        <svg className="h-16 w-16" viewBox="0 0 73 72" fill="none">
          <rect x="0.5" width="72" height="72" rx="36" fill="#111536" fillOpacity="0.4" />
          <path
            d="M44.0306 31.7194L38.7806 26.4694C38.7109 26.3997 38.6282 26.3445 38.5371 26.3069C38.4461 26.2693 38.3485 26.2499 38.25 26.25H29.25C28.8522 26.25 28.4706 26.408 28.1893 26.6893C27.908 26.9706 27.75 27.3522 27.75 27.75V44.25C27.75 44.6478 27.908 45.0294 28.1893 45.3107C28.4706 45.592 28.8522 45.75 29.25 45.75H42.75C43.1478 45.75 43.5294 45.592 43.8107 45.3107C44.092 45.0294 44.25 44.6478 44.25 44.25V32.25C44.2501 32.1515 44.2307 32.0539 44.1931 31.9629C44.1555 31.8718 44.1003 31.7891 44.0306 31.7194ZM39 28.8103L41.6897 31.5H39V28.8103ZM42.75 44.25H29.25V27.75H37.5V32.25C37.5 32.4489 37.579 32.6397 37.7197 32.7803C37.8603 32.921 38.0511 33 38.25 33H42.75V44.25Z"
            fill="white"
          />
        </svg>

        <div className="mt-4 break-all text-center font-semibold text-gray-700">{doc.name}</div>

        {warning ? (
          <div className="mb-2 mt-2 break-all text-center text-sm text-gray-700">{warning}</div>
        ) : null}

        <Button
          variant="primary"
          className="mt-4"
          leadingIcon={<DownloadIcon className="size-4" />}
          onClick={() => send({ type: 'downloadDocument', documentId: doc.id })}
        >
          Download
        </Button>
      </div>
    </div>
  )
}

function BinderLoadingState() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading binder"
      className="flex min-h-0 w-full flex-1 items-center justify-center bg-white"
    >
      <div className="flex flex-col items-center gap-4 text-gray-700">
        <Spinner className="size-8 fill-blue-500 text-blue-100" />
      </div>
    </div>
  )
}

function DocumentSpinner({ caption }: { caption?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-stone-200">
      <div role="status" aria-live="polite" aria-busy="true" aria-label={caption ?? 'Loading'}>
        <svg
          aria-hidden="true"
          className="h-16 w-16 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">{caption ?? 'Loading'}</span>
      </div>
      <div className="text-center text-gray-700">{caption ?? ''}</div>
    </div>
  )
}

function ActivityFeedButton() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <PopoverMenu
      isOpen={isOpen}
      isControlled={true}
      onOpenChange={setIsOpen}
      trigger={
        <div>
          {/* workaround for button clicks getting swallowed by the popover menu */}
          {/* adding a button directly swallows the click event */}
          {/* TODO: look into PopoverMenu implementation and see how we want to fix this */}
          <Tip title="Activity feed">
            <Button
              variant="icon"
              aria-label="Open activity feed"
              leadingIcon={<RowsIcon className="size-4" />}
              onClick={() => setIsOpen(true)}
            />
          </Tip>
        </div>
      }
    >
      <ActivityFeedPopup onClose={() => setIsOpen(false)} />
    </PopoverMenu>
  )
}

function ActivityFeedPopup({ onClose }: { onClose: () => void }) {
  const events = useSelect('stFolder', selActivityFeedEvents)

  return (
    <ActivityFeedList title="Activity" onClose={onClose}>
      {events.map((e) => (
        <ActivityFeedItem
          key={e.id}
          title={formatActor(e)}
          description={formatEventDescription(e)}
          time={formatISO8601RelativeDateTime(e.time)}
          warning={
            e.type == 'client_organizer_email_sent' && e.status == 'failed'
              ? `Failed to send: ${e.errorMessage}`
              : undefined
          }
        />
      ))}
    </ActivityFeedList>
  )
}

function formatActor(e: ExpandedActivityFeedEvent) {
  if (!e.actorSide || !e.actorName) {
    if (
      e.type == 'client_organizer_email_sent' &&
      e.messageType == 'client_organizer_submit_reminder'
    ) {
      return ''
    }

    return '<removed>'
  }

  const side = e.actorSide == 'firm' ? 'Firm' : 'Client'

  return `${e.actorName} (${side})`
}

function formatEventDescription(e: ExpandedActivityFeedEvent) {
  switch (e.type) {
    case 'document_uploaded':
      return (
        <span>
          Uploaded document <b>{e.document?.name}</b>
        </span>
      )
    case 'document_deleted':
      return (
        <span>
          Deleted document <b>{e.document?.name}</b>
        </span>
      )
    case 'client_organizer_started':
      return 'Started organizer'
    case 'client_organizer_submitted':
      return 'Submitted organizer'
    case 'contract_submitted':
      return (
        <span>
          Signed document <b>{e.document?.name}</b>
        </span>
      )
    case 'client_organizer_email_sent':
      switch (e.messageType) {
        case 'client_organizer_invitation':
          return `Sent organizer to ${humanJoin(e.recipientEmails, 'and')}`
        case 'client_organizer_document_request':
          return `Sent document request to ${humanJoin(e.recipientEmails, 'and')}`
        case 'client_organizer_submit_reminder':
          return `Reminder was sent to ${humanJoin(e.recipientEmails, 'and')}`
        case 'tax_return_delivery':
          return `Sent tax return delivery to ${humanJoin(e.recipientEmails, 'and')}`
      }
    case 'documents_exported':
      return 'Exported documents'
  }
}
